import React from 'react';

/**
 * MayaErrorBoundary
 * -----------------
 * Local boundary around the sign-up Maya chat. If any runtime error occurs
 * (e.g. a browser Speech-API edge case), the rest of the registration page
 * stays intact and the teen gets a compact "try again" panel instead of a
 * blanked screen. The real error is logged to /api/telemetry/frontend-crash
 * so the true root cause is captured from production.
 */
class MayaErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('[MayaErrorBoundary] Maya chat crashed:', error, info);
    try {
      const url = (process.env.REACT_APP_BACKEND_URL || '') + '/api/telemetry/frontend-crash';
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: (error && error.message) || 'unknown',
          stack: (error && error.stack) || '',
          component_stack: (info && info.componentStack) || '',
          path: typeof window !== 'undefined' ? window.location.pathname : '',
          context: 'maya_registration_chat',
          ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (_e) { /* best-effort only */ }
  }

  handleRetry = () => {
    // Bump the key so the Maya chat remounts fresh.
    this.setState((s) => ({ hasError: false, retryKey: (s.retryKey || 0) + 1 }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="maya-error-fallback"
          className="rounded-2xl border border-purple-100 bg-white p-6 text-center"
        >
          <p className="text-gray-800 font-semibold mb-1">Maya hit a snag.</p>
          <p className="text-sm text-gray-500 mb-4">
            No data was lost. Tap below to pick up right where you left off — you can also just type instead of using the mic.
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            data-testid="maya-error-retry-btn"
            className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold"
          >
            Try again
          </button>
        </div>
      );
    }
    return (
      <React.Fragment key={this.state.retryKey || 0}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

export default MayaErrorBoundary;
